import type { ReactNode } from 'react'

interface EmptyStateProps {
  /** One emoji. Not an illustration — see below. */
  emoji: string
  title: string
  body?: string
  action?: ReactNode
}

/**
 * The empty state: a big emoji, a line, and the way out.
 *
 * **Emoji, never an illustration.** Three reasons, and they are the design
 * system's: an illustration set is art nobody has commissioned yet, it doubles
 * in size the moment dark mode exists, and it has to be redrawn for every
 * locale where the metaphor does not land. An emoji is none of those, and it
 * renders in the user's own system font.
 *
 * The emoji is `aria-hidden` and the meaning lives in the title — a screen
 * reader announcing "bowl with spoon" adds nothing to "belum ada catatan".
 *
 * SUAP-0103.
 */
export function EmptyState({ emoji, title, body, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
      <span aria-hidden className="text-5xl leading-none">
        {emoji}
      </span>
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {body === undefined ? null : <p className="max-w-xs text-sm text-ink-soft">{body}</p>}
      {action === undefined ? null : <div className="pt-1">{action}</div>}
    </div>
  )
}
